/**
 * 平台枚举 —— 分类大类与快照来源
 *
 * 依据：架构 §4.2 · `DR-012`（曲线只用真实快照）
 */

// ─────────────────────────── 分类大类 ───────────────────────────

/**
 * 分类大类（`categories.kind`）—— 2 值。
 *
 * 🔴 **子分类 `kind` 恒等于父级**（架构 §4.2 冻结要点）——
 * 不存在「支出下的收入子类」这种组合，服务端写入时须校验。
 *
 * ⚠️ **分类体系本身（L1 / L2 的 `id` 与名称）不在本文件** ——
 * 它是**身份层**，落在 `src/enums/categories.ts`，由构建期 `tools/gen-categories.ts`
 * 与设计资产的**呈现层**合并（架构 §4.6「身份 × 呈现分工」）。本文件只管 `kind` 这个**维度**。
 */
export const CategoryKind = {
  Expense: 'expense',
  Income: 'income',
} as const;
export type CategoryKind = (typeof CategoryKind)[keyof typeof CategoryKind];

// ────────────────────────── 快照来源 ──────────────────────────

/**
 * 净资产快照来源（`net_worth_snapshots.origin`）。
 *
 * 🔴 **曲线只用真实快照**（`DR-012`）：**禁估算 / 平推 / 插值 / 外推**。
 * - `Live` = 每日 cron 真实落点 —— **唯一可用于画曲线的来源**；
 * - `Backfill` = 回填点，**必须同时看 `estimated` 标记**：
 *   `origin='backfill'` 且 `estimated=1` 的行是「按当前值平推」的产物，
 *   **正是 `DR-012` 点名废弃者** ⇒ 🔴 **迁移时不得迁入**（`DR-017` / `P域PRD` `AC-P3.5`）。
 *
 * 🔴 **`live` 不可被 `backfill` 覆盖**（架构 §4.2 唯一键 `(ledgerId, date)`）。
 */
export const SnapshotOrigin = {
  Live: 'live',
  Backfill: 'backfill',
} as const;
export type SnapshotOrigin = (typeof SnapshotOrigin)[keyof typeof SnapshotOrigin];
